import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { BACKEND_URL, getErrorMessage } from '../constants';

export default function useDocumentActions({ headers, fetchSimulations, setSavedSimulation }) {
  const [downloadingDoc, setDownloadingDoc] = useState(null);
  const [confirmingVirement, setConfirmingVirement] = useState(false);
  const [confirmingFTPE, setConfirmingFTPE] = useState(false);

  const handleDownloadDocument = async (reference, docType, filename) => {
    setDownloadingDoc(docType);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/domium/documents/${reference}/${docType}`,
        { headers, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url; link.setAttribute('download', filename);
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${filename} telecharge`);
    } catch { toast.error(`Erreur telechargement ${filename}`); }
    finally { setDownloadingDoc(null); }
  };

  const handleDownloadReceipt = async (reference) => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/domium/documents/${reference}/receipt`,
        { headers, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url; link.setAttribute('download', `Recu_DOMIUM_${reference}.pdf`);
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Recu de paiement telecharge');
    } catch { toast.error('Recu non disponible'); }
  };

  const handleDownloadRecuESS = async (reference) => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/domium/subscribe/${reference}/download/recu-ess`,
        { headers, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url; link.setAttribute('download', `Recu_Contribution_ESS_${reference}.pdf`);
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Recu de contribution solidaire ESS telecharge');
    } catch { toast.error('Recu non disponible'); }
  };

  const handlePrintDocumentsList = (reference) => {
    const html = `<!DOCTYPE html><html lang="fr"><head><meta charset="UTF-8"><title>Documents - ${reference}</title>
<style>@page{size:A4;margin:20mm}@media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact}}
body{font-family:'Segoe UI',Arial,sans-serif;font-size:12pt;line-height:1.6;color:#1e293b}
.header{text-align:center;margin-bottom:30px;padding-bottom:20px;border-bottom:3px solid #059669}
.header h1{color:#059669;font-size:22pt;margin:0 0 10px 0}.header .subtitle{color:#64748b;font-size:11pt}
.header .reference{background:#f1f5f9;padding:8px 15px;border-radius:5px;display:inline-block;margin-top:10px;font-family:monospace}
.section{margin:25px 0}.section-title{font-size:14pt;font-weight:bold;color:#1e40af;margin-bottom:15px;padding-bottom:8px;border-bottom:2px solid #e2e8f0}
.documents-list{list-style:none;padding:0;margin:0}
.documents-list li{padding:12px 15px;margin:8px 0;background:#f8fafc;border-left:4px solid #059669;border-radius:0 8px 8px 0;display:flex;align-items:flex-start;gap:10px}
.documents-list li::before{content:"\\2610";font-size:16pt;color:#059669}
.attention-box{background:#fef3c7;border:2px solid #f59e0b;border-radius:10px;padding:20px;margin-top:30px}
.attention-box h3{color:#b45309;margin:0 0 10px 0;font-size:14pt}.attention-box ul{margin:10px 0 0 20px}
.attention-box li{margin:8px 0;color:#92400e}
.footer{margin-top:40px;padding-top:20px;border-top:2px solid #e2e8f0;text-align:center;font-size:9pt;color:#64748b}</style></head>
<body><div class="header"><h1>DOCUMENTS A FOURNIR</h1><div class="subtitle">Souscription DOS DOMIUM - Dommages-Ouvrage</div>
<div class="reference">Reference : ${reference}</div></div>
<div class="section"><div class="section-title">Documents requis pour l'instruction du dossier</div>
<ul class="documents-list"><li>Permis de construire</li><li>Declaration d'ouverture de chantier</li>
<li>Plans d'architecture (coupes, distribution)</li><li>Plan de masse</li>
<li>Etudes techniques (Structure, etude de sol, etude d'assainissement, conformite...)</li>
<li>Attestation d'assurances des intervenants a la construction</li>
<li>Devis des intervenants a la construction</li></ul></div>
<div class="attention-box"><h3>ATTENTION - A la reception des travaux</h3><p>Transmettre imperativement :</p>
<ul><li>PV de reception des travaux sans reserves</li>
<li>Le cas echeant, la DAACT (Declaration Attestant l'Achevement et la Conformite des Travaux)</li></ul></div>
<div class="footer"><p>OBJECTIF SCOP OUTREMER - COPPAM Garanties Solidaires</p>
<p>Document genere le ${new Date().toLocaleDateString('fr-FR')} a ${new Date().toLocaleTimeString('fr-FR')}</p></div></body></html>`;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.onload = () => printWindow.print();
    toast.success('Impression lancee');
  };

  const handleConfirmVirement = async (reference) => {
    setConfirmingVirement(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/domium/subscribe/admin/${reference}/confirm-payment`, {}, { headers });
      if (res.data.success) {
        toast.success('Virement confirme ! Recu ESS genere.');
        await fetchSimulations();
        try {
          const u = await axios.get(`${BACKEND_URL}/api/domium/subscribe/${reference}`, { headers });
          if (u.data?.success && u.data?.simulation) setSavedSimulation(u.data.simulation);
        } catch {}
        try {
          const r = await axios.get(`${BACKEND_URL}/api/domium/subscribe/${reference}/download/recu-ess`, { headers, responseType: 'blob' });
          const url = window.URL.createObjectURL(new Blob([r.data]));
          const link = document.createElement('a');
          link.href = url; link.setAttribute('download', `Recu_Contribution_ESS_${reference}.pdf`);
          document.body.appendChild(link); link.click(); link.remove();
          window.URL.revokeObjectURL(url);
        } catch {}
      }
    } catch (error) { toast.error(getErrorMessage(error, 'Erreur confirmation virement')); }
    finally { setConfirmingVirement(false); }
  };

  const handleConfirmFTPEPayment = async (reference) => {
    setConfirmingFTPE(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/domium/subscribe/admin/${reference}/confirm-ftpe-payment`, {}, { headers });
      if (res.data.success) {
        toast.success('Paiement FTPE confirme !');
        try {
          const u = await axios.get(`${BACKEND_URL}/api/domium/subscribe/${reference}`, { headers });
          if (u.data?.success && u.data?.simulation) setSavedSimulation(u.data.simulation);
        } catch {}
        try {
          const r = await axios.get(`${BACKEND_URL}/api/domium/documents/${reference}/attestation-ftpe-draft`, { headers, responseType: 'blob' });
          const url = window.URL.createObjectURL(new Blob([r.data]));
          const link = document.createElement('a');
          link.href = url; link.setAttribute('download', `Attestation_FTPE_Signee_${reference}.pdf`);
          document.body.appendChild(link); link.click(); link.remove();
          window.URL.revokeObjectURL(url);
        } catch {}
      }
    } catch (error) { toast.error(getErrorMessage(error, 'Erreur confirmation FTPE')); }
    finally { setConfirmingFTPE(false); }
  };

  return {
    downloadingDoc, confirmingVirement, confirmingFTPE,
    handleDownloadDocument, handleDownloadReceipt, handleDownloadRecuESS,
    handlePrintDocumentsList, handleConfirmVirement, handleConfirmFTPEPayment,
  };
}
